import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Target, ArrowRight, ClipboardList, FlaskConical, TrendingUp, Utensils, X } from "lucide-react";

/**
 * NextStepCard — single most valuable next action for the user.
 *
 * Backend decides the priority: complete profile → upload labs → upgrade plan
 * → improve DIL. Matches the "Your next step" spec from the pricing UX brief.
 */
const ICON_MAP = {
  complete_profile: ClipboardList,
  upload_labs: FlaskConical,
  upgrade_plan: TrendingUp,
  improve_dil: Utensils,
};
const COLOR_MAP = {
  complete_profile: "#0EA5E9",
  upload_labs: "#8B5CF6",
  upgrade_plan: "#F59E0B",
  improve_dil: "#10B981",
};

export default function NextStepCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";
  const [step, setStep] = useState(null);
  // iter327Y — persistent dismissal: store list of dismissed step.key values
  // in localStorage so user can permanently hide each "next step" type.
  const [dismissedKeys, setDismissedKeys] = useState(() => {
    try {
      const raw = localStorage.getItem("nutramilo_dismissed_next_steps");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  const fetchStep = React.useCallback(async () => {
    try {
      // iter265: cookie-based auth — axios withCredentials handles it.
      const { data } = await axios.get(`${API}/subscription/next-step`);
      setStep(data);
    } catch { /* silent */ }
  }, [API]);

  useEffect(() => {
    fetchStep();
    // Re-fetch when user performs the key actions that mutate the "next step" logic:
    // profile completion, lab upload, meal logging. Without this, the card stays
    // on a stale action like "Upload labs" even after the user just uploaded.
    const onChange = () => fetchStep();
    // iter327Z — re-read dismissedKeys when user restores cards via Settings.
    const onRestore = () => {
      try {
        const raw = localStorage.getItem("nutramilo_dismissed_next_steps");
        setDismissedKeys(raw ? JSON.parse(raw) : []);
      } catch { setDismissedKeys([]); }
    };
    window.addEventListener("meal-added", onChange);
    window.addEventListener("meal-log-changed", onChange);
    window.addEventListener("lab-uploaded", onChange);
    window.addEventListener("profile-updated", onChange);
    window.addEventListener("nutramilo:cards-restored", onRestore);
    return () => {
      window.removeEventListener("meal-added", onChange);
      window.removeEventListener("meal-log-changed", onChange);
      window.removeEventListener("lab-uploaded", onChange);
      window.removeEventListener("profile-updated", onChange);
      window.removeEventListener("nutramilo:cards-restored", onRestore);
    };
  }, [fetchStep]);

  const handleDismiss = () => {
    if (!step?.key) return;
    const next = Array.from(new Set([...dismissedKeys, step.key]));
    setDismissedKeys(next);
    try { localStorage.setItem("nutramilo_dismissed_next_steps", JSON.stringify(next)); } catch {}
  };

  if (!step || dismissedKeys.includes(step.key)) return null;

  const Icon = ICON_MAP[step.key] || Target;
  const color = COLOR_MAP[step.key] || "#0EA5E9";

  const handleCta = () => {
    const route = step.cta_route || "/dashboard";
    // Map known routes to in-page anchor scroll. Going to a new route won't
    // trigger scroll on its own because Dashboard doesn't read ?tab= yet.
    if (step.key === "upload_labs") {
      navigate("/dashboard");
      setTimeout(() => {
        const el = document.getElementById("lab-upload")
          || document.querySelector('[data-testid="lab-upload-card"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
      return;
    }
    if (step.key === "improve_dil") {
      navigate("/dashboard");
      // Force-open the collapsible Meal Log panel AND scroll to it.
      setTimeout(() => window.dispatchEvent(new Event("dashboard:open-meal-log")), 120);
      return;
    }
    navigate(route);
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${
        isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : "bg-white border-gray-100 shadow-sm"
      }`}
      data-testid="next-step-card"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] uppercase tracking-[0.14em] font-semibold mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? "Следваща стъпка" : "Next step"}
          </p>
          <h3 className={`text-sm font-bold mb-0.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} data-testid="next-step-title">
            {bg ? step.title_bg : step.title_en}
          </h3>
          <p className={`text-xs leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg ? step.description_bg : step.description_en}
          </p>
          <button
            onClick={handleCta}
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold transition-colors"
            style={{ color }}
            data-testid="next-step-cta"
          >
            {bg ? step.cta_bg : step.cta_en}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className={`self-start -mt-1 -mr-1 p-1 rounded opacity-50 hover:opacity-100 transition-opacity ${isDark ? "text-gray-400" : "text-gray-500"}`}
          data-testid="next-step-dismiss"
          aria-label={bg ? "Скрий" : "Dismiss"}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
